import crypto from 'crypto';
import { stringify } from 'safe-stable-stringify';
import { MapLike } from '../../core';
import { isNullish } from '../../Validation';
export * from './identity';


/**
 * Returns hash of object based on 'safe-stable-stringify' package.
 * 
 * @description
 * 
 * This function does something similar to JSON.stringify, but more than it.  
 * It then converts it to a safe, short hash via the `crypto` module
 * 
 * @example
 * ```
 * import crypto from 'crypto'
 * import hash from 'stable-hash'
 * 
 * const longHash = stringify(anyJavaScriptValueHere)
 * const displayableHash = Buffer.from(longHash).toString('base64')
 * const shortHash = crypto.createHash('sha256').update(longHash).digest('hex')
 * ```
 * 
 * Was previously using the `object-hash` package, but this had issues and didn't produce a stable hashs
 * 
 * `seenObjects` is deprecated and unused
 */
export const getObjectHash = (ob: any | undefined, /** @deprecated */ seenObjects: any[] = []) => {
  const stringHash: string = ob ? (stringify(ob) ?? '~') : '~'
  const shortHash: string = crypto.createHash('sha256').update(stringHash).digest('hex')

  return shortHash
}

export const objectsMatchByHash = (template: MapLike<any>, ob: MapLike<any>): boolean => getObjectHash(template) === getObjectHash(ob)
const oMatchDebug: boolean = false

/**
 * @underReview the logic here is likely to be replaced by objectsMatchByHash when proven
 * @function objectsMatch tests whether the attributes in ob that match the attributes of template have the same value
 * @warning this may produce unexpected results where ob has been rehydated and undefined attributes has been omitted,
 * @warning this will match elements in arrays based on the number of items in an array nested in template, but is incorrect currently if the same array in ob has same first items but then additional items
 * @param template
 * @param ob
 * @returns
 */
export const objectsMatch = (template: MapLike<any>, ob: MapLike<any>): boolean => {
  if (!template || !ob) {
    console.log(`WARN: unhandled presentation of undefined params to objectsMatch`, { template, ob })
    return false
  }
  if (template === ob) {
    return true
  }
  return Object.entries(template).reduce((acc, [k, v]) => {
    if (typeof v !== 'function') {
      oMatchDebug && console.log(`matching ${k} on ${JSON.stringify(ob[k])} with ${JSON.stringify(v)}`)
      return acc && ((ob[k] === undefined && v === undefined) || (ob[k] !== undefined && (typeof v === 'object' ? objectsMatch(v, ob[k]) : v === ob[k])))
    } else {
      return acc
    }
  }, true as boolean)
}

export const objectsMatchDebug = (template: MapLike<any>, ob: MapLike<any>): MapLike<any> => {
  return Object.entries(template).reduce((acc, [k, v]) => {
    if (ob[k] !== undefined) {
      if (typeof v === 'object') {
        acc[k] = objectsMatchDebug(v, ob[k])
      } else {
        if (v !== ob[k]) {
          acc[k] = ob[k]
        }
      }
    }
    return acc
  }, {} as MapLike<any>)
}

export const diffObjects = (template: MapLike<any>, ob: MapLike<any>): MapLike<any> => {
  if (template === ob) {
    return {}
  }

  return Object.entries(template).reduce((acc, [k, v]) => {
    if (typeof v !== 'function') {
      if (v !== undefined && ob[k] === undefined) {
        acc[k] = `${k} non existant`
      } else if (v !== null && ob[k] === null) {
        acc[k] = `${k} null object`
      } else if (typeof v === 'object' && v !== null) {
        acc[k] = diffObjects(v, ob[k])
      } else if (v instanceof Array) {
        acc[k] = diffObjects(v, ob[k])
      } else if (v !== ob[k]) {
        acc[k] = `${v} : ${ob[k]}`
      } else {
      }
    }

    return acc
  }, {} as MapLike<any>)
}

/** Returns true if the ob has at least all the ( nested keys ) of the template */
export const matchKeysOnly = (template: MapLike<any> | null, ob: MapLike<any> | null): boolean => {
  if (!isNullish(template)) {
    return Object.entries(template).reduce((acc, [k, v]) => {
      const obIsNullish = isNullish(ob)
      if (obIsNullish) {
        // template val is not nullish but ob is
        return false
      } else {
        // both not nullish
        return acc && Object.keys(ob).includes(k) && (typeof v === 'object' ? matchKeysOnly(v, ob[k]) : true)
      }
    }, true as boolean)
  } else {
    // both null or ob is not an object
    return typeof ob !== 'object' || isNullish(ob)
  }
}

/** Matches keys AND values, initally desgined for templates where fields are undefined'd out. This is somewhat misleadingly named, use matchKeysOnly to test for keys but not values */
export const matchObjectKeys = (template: MapLike<any>, ob: MapLike<any>): boolean => {
  return Object.entries(template).reduce((acc, [k, v]) => {
    return acc && Object.keys(ob).includes(k) && (typeof v === 'object' ? matchObjectKeys(v, ob[k]) : v === undefined || v === ob[k])
  }, true as boolean)
}

export const matchObjectKeysDebug = (template: MapLike<any>, ob: MapLike<any>): boolean => {
  return Object.entries(template).reduce((acc, [k, v]) => {
    const includesK = Object.keys(ob).includes(k)
    if (!(typeof v === 'object')) {
    } else {
      const unmatchedKeys = filterSetBfromA(Object.keys(v), Object.keys(ob[k] ?? {}))
      if (unmatchedKeys.length > 0) console.log(`${k} is an object with unmatched keys ${unmatchedKeys.join(', ')}`)
    }
    const res = acc && includesK && (typeof v === 'object' ? matchObjectKeysDebug(v, ob[k]) : v === undefined || v === ob[k])
    return res
  }, true as boolean)
}

const isMapLike = (x: any): x is MapLike<any> => x && typeof x === 'object' // purely to make objectsMatch work with setContains
/** this was flawed and improved but doesnt deal with arrays */
export const setAContainsSomeB = <T>(A: T[], B: T[]) => B.some((b) => A.find((a) => (isMapLike(a) && isMapLike(b) ? objectsMatch(a, b) : a === b)))

export const setAContainsAllB = <T>(A: T[], B: T[]) => B.every((b) => A.find((a) => (isMapLike(a) && isMapLike(b) ? objectsMatch(a, b) : a === b)))

export const setAContainsOnlySomeB = <T>(A: T[], B: T[]) => A.every((a) => B.find((b) => (isMapLike(a) && isMapLike(b) ? objectsMatch(a, b) : a === b)))

export const filterSetBfromA = <T>(A: T[], B: T[]) => A.filter((a) => !B.find((b) => (isMapLike(a) && isMapLike(b) ? objectsMatch(a, b) : a === b)))
